import type { BreakErrorType } from "../../MissedPunches/utils/validateBreakTimestamps";
import type { ClockInErrorType } from "../../MissedPunches/utils/validateClockInTimestamp";
import type { ClockOutErrorType } from "../../MissedPunches/utils/validateClockOutTimestamp";

/**
 * All validation error types across clock actions.
 */
export type TimestampErrorType = ClockInErrorType | ClockOutErrorType | BreakErrorType;

/**
 * Boundary errors represent EPAT/LPDT violations - timestamps that fall outside
 * the system's allowable range based on geofencing, verified times, or shift schedule.
 *
 * These are distinct from "user-fixable" errors like "clock out before clock in"
 * which result from the user's own selections within the current session.
 */
const BOUNDARY_ERROR_TYPES = [
  // Clock-in boundary errors
  "GEOFENCE_ERROR",
  "WAY_TOO_EARLY",
  "TOO_EARLY_10_MIN",
  "NO_EARLY_CLOCK_IN",
  "REDUCES_VERIFIED_TIME",

  // Clock-out boundary errors
  "TOO_LATE_CLOCK_OUT",
] as const;

type BoundaryErrorType = (typeof BOUNDARY_ERROR_TYPES)[number];

/**
 * Type guard to check if an error type is a boundary error.
 * Returns true and narrows the type if the error is a boundary violation.
 */
export function isBoundaryError(
  errorType: TimestampErrorType | undefined
): errorType is BoundaryErrorType {
  if (!errorType) {
    return false;
  }

  return (BOUNDARY_ERROR_TYPES as readonly string[]).includes(errorType);
}
